import type { Product, RecordRow } from "./api";

export const RECORD_COLUMNS = ["approved_date", "entity_id", "book_currency", "production_key", "sku", "qty", "cost", "basis", "source", "derived_from_sku", "tracking", "variant", "location", "vs_previous_pct"] as const;

export type PvrOptions = { tracking: string[]; variant: string[]; location: string[] };

export function pvrOptions(products: Product[]): PvrOptions {
  const distinct = (key: "tracking" | "variant" | "location") => [...new Set(products.map((product) => product[key]).filter((value): value is string => Boolean(value)))].sort((a, b) => a.localeCompare(b));
  return { tracking: distinct("tracking"), variant: distinct("variant"), location: distinct("location") };
}

export function channelWords(sku: string, products: Product[]): string {
  const channels = [...new Set(products.filter((product) => product.xero_sku === sku).map((product) => product.channel).filter((channel): channel is string => Boolean(channel)))].sort((a, b) => a.localeCompare(b));
  return channels.length ? channels.join(", ") : "-";
}

export function searchRows(rows: RecordRow[], products: Product[], q: string): RecordRow[] {
  const query = q.trim().toLocaleLowerCase();
  if (!query) return rows;
  return rows.filter((row) => [
    row.sku,
    row.tracking,
    row.variant,
    row.location,
    channelWords(row.sku, products),
  ].some((value) => value?.toLocaleLowerCase().includes(query)));
}

export function sortByDate(rows: RecordRow[], direction: "asc" | "desc"): RecordRow[] {
  const factor = direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const byDate = left.approved_date.localeCompare(right.approved_date);
    if (byDate !== 0) return byDate * factor;
    return left.created_at.localeCompare(right.created_at) * factor;
  });
}

export function skuOptions(products: Product[]): Array<{ value: string; label: string; hint: string }> {
  const bySku = new Map<string, Product>();
  for (const product of products) if (!bySku.has(product.xero_sku)) bySku.set(product.xero_sku, product);
  return [...bySku.values()]
    .sort((left, right) => left.xero_sku.localeCompare(right.xero_sku))
    .map((product) => ({
      value: product.xero_sku,
      label: product.xero_sku,
      hint: [product.tracking, product.variant, product.location].filter(Boolean).join(" · "),
    }));
}

export function historySeries(rows: RecordRow[]): { currencies: string[]; points: Array<Record<string, string | number>> } {
  const sorted = sortByDate(rows, "asc");
  const currencies = [...new Set(sorted.map((row) => row.book_currency))].sort((left, right) => left.localeCompare(right));
  const byDate = new Map<string, Record<string, string | number>>();
  for (const row of sorted) {
    const point = byDate.get(row.approved_date) ?? { approved_date: row.approved_date };
    point[row.book_currency] = Number(row.cost);
    byDate.set(row.approved_date, point);
  }
  return { currencies, points: [...byDate.values()] };
}

export function toCsvRows(rows: RecordRow[], products: Product[]): string[][] {
  return [
    [...RECORD_COLUMNS, "channel"],
    ...rows.map((row) => [
      ...RECORD_COLUMNS.map((column) => {
        const value = column === "derived_from_sku" ? row.derived_from_sku.join(",") : (row as Record<string, unknown>)[column];
        return value === null || value === undefined ? "" : String(value);
      }),
      channelWords(row.sku, products),
    ]),
  ];
}

export function basisWords(row: RecordRow): string {
  if (row.basis === "derived") {
    const units = row.derived_from_sku.map((sku) => /-W(\d+)$/.exec(sku)?.[1] ?? "?");
    return `derived from ${row.derived_from_sku.join(", ")} ÷ ${units.length === 1 ? units[0] : units.join("/")}`;
  }
  return row.source === "legacy_sheet" ? `produced (legacy ${row.legacy_ref ?? ""})`.trim() : "produced";
}

export function qtyWords(row: RecordRow): string {
  return row.basis === "derived" ? "—" : new Intl.NumberFormat("en-GB", { maximumFractionDigits: 4 }).format(Number(row.qty));
}

export function costWords(row: RecordRow): string { return Number(row.cost).toFixed(4); }

export function defaultRange(today: string): { since: string; until: string } {
  const [year, month, day] = today.split("-").map(Number);
  const since = new Date(Date.UTC(year - 1, month - 1, day));
  return { since: since.toISOString().slice(0, 10), until: today };
}

export function canDrill(row: RecordRow): boolean { return row.source === "run" && row.production_key !== null; }
